import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { actionTones } from '../../constants/actionTones';
import {
  Plus, Edit2, Trash2, Archive, Users, DollarSign,
  CalendarDays, Search, CheckCircle,
  AlertCircle, BookOpen, Loader, Sparkles, Share2, UserPlus, ChevronLeft,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { academyAPI } from '../../../services/api';
import CreateProgramModal from '../../components/CreateProgramModal';
import AiFlyerModal from '../../components/AiFlyerModal';
import ProgramSessionsPanel from '../program/ProgramSessionsPanel';
import AttachPriceModal from '../../../payments/components/PaymentPriceModal';
import ShareModal from '../../../components/common/ShareModal';
import ProgramEnrollmentModal from '../../components/ProgramEnrollmentModal';
import { feeUnit, resolveMediaUrl } from '../../../utils/helpers';

// ─── Status helpers ───────────────────────────────────────────────────────────

function getProgramStatus(program) {
  if (!program.isActive) return 'archived';
  const now = new Date();
  const start = program.startDate ? new Date(program.startDate) : null;
  const end = program.endDate ? new Date(program.endDate) : null;
  if (end && end < now) return 'ended';
  if (start && start > now) return 'upcoming';
  return 'active';
}

/* ⚠️ CATEGORICAL, NOT BRAND. Green means active, blue means upcoming, grey
   means ended — the colours name a status and must not follow the academy's
   theme. Module scope too, so there is no theme to read here anyway. */
const STATUS_CONFIG = {
  active:   { label: 'Active',    bg: 'bg-green-100',  text: 'text-green-700',  dot: 'bg-green-500'  },
  upcoming: { label: 'Upcoming',  bg: 'bg-blue-100',   text: 'text-blue-700',   dot: 'bg-blue-500'   },
  ended:    { label: 'Ended',     bg: 'bg-gray-100',   text: 'text-gray-500',   dot: 'bg-gray-400'   },
  archived: { label: 'Archived',  bg: 'bg-red-50',     text: 'text-red-500',    dot: 'bg-red-400'    },
};

const LEVEL_COLORS = {
  beginner:     'bg-emerald-100 text-emerald-700',
  intermediate: 'bg-yellow-100 text-yellow-700',
  advanced:     'bg-red-100 text-red-700',
};

function fmt(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}


// ─── ProgramCard ──────────────────────────────────────────────────────────────

function ProgramCard({ program, onEdit, onFlyer, onShare, onAssign, onSessions, onToggleActive, onDelete, confirmDeleteId, setConfirmDeleteId, theme, isDark }) {
  // Sessions are the bookable unit only when an offering sells per occurrence.
  // A term course has no per-night roster, so the action would mean nothing.
  const sellsPerSession = program.offering?.registrationScope === 'occurrence';
  const tone = actionTones(isDark);
  const status = getProgramStatus(program);
  const sc = STATUS_CONFIG[status];
  const levelColor = LEVEL_COLORS[program.level] || 'bg-gray-100 text-gray-600';
  const enrolled = program.enrollmentCount ?? 0;
  const max = program.maxParticipants;
  const fillPct = max ? Math.min(100, Math.round((enrolled / max) * 100)) : null;
  const isFull = max && enrolled >= max;

  return (
    <div className={`${theme.card} border ${theme.border} rounded-xl flex flex-col overflow-hidden`}>
      {/* The programme's own photograph.
          It is set on the programme and shown on the public Programs tab, but
          this card — the one the owner actually manages from — never displayed
          it, so the image you had just uploaded appeared nowhere in Settings
          and there was no way to tell from here which programmes had one.
          Rendered only when there is one: an empty band on every card would
          cost more than it gives. */}
      {program.imageUrl && (
        <div className="relative w-full h-28 shrink-0">
          <img
            src={resolveMediaUrl(program.imageUrl)}
            alt=""
            className="absolute inset-0 w-full h-full object-cover"
          />
        </div>
      )}

      {/* Top accent */}
      <div className={`h-1 w-full ${
        status === 'active'   ? 'bg-green-500' :
        status === 'upcoming' ? 'bg-blue-500'  :
        status === 'ended'    ? 'bg-gray-400'  : 'bg-red-400'
      }`} />

      <div className="p-5 flex flex-col gap-4 flex-1">
        {/* Header — the title/tag block is height-reserved (2 title lines +
            2 tag rows) so the Dates / Enrollment / Registration rows line up
            across every card in the grid regardless of name or tag length. */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0 min-h-[6rem]">
            <h3 className={`font-bold text-base ${theme.text} leading-snug line-clamp-2`}>{program.name}</h3>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {program.sportName && (
                <span className={`px-2 py-0.5 ${theme.tag} rounded text-xs`}>{program.sportName}</span>
              )}
              {program.level && (
                <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${levelColor}`}>
                  {program.level}
                </span>
              )}
              {program.ageGroup && (
                <span className={`px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs`}>{program.ageGroup}</span>
              )}
            </div>
          </div>
          {/* Status badge */}
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${sc.bg} ${sc.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
            {sc.label}
          </span>
        </div>

        {/* Description — always rendered (and two lines tall) even when empty,
            so a program without one doesn't shift the rows below out of
            alignment with its neighbours. */}
        <p className={`text-sm ${theme.textSecondary} line-clamp-2 min-h-[2.625rem]`}>
          {program.description}
        </p>

        {/* Dates + fee row */}
        <div className={`flex flex-wrap items-center gap-3 text-xs ${theme.textMuted}`}>
          {(program.startDate || program.endDate) && (
            <span className="flex items-center gap-1">
              <CalendarDays size={12} />
              {fmt(program.startDate) ?? '—'}
              {' → '}
              {fmt(program.endDate) ?? 'Ongoing'}
            </span>
          )}
          <span className="flex items-center gap-1">
            <DollarSign size={12} />
            {/* From the price tag — `fee` was a programme column the API read
                and wrote and no payment path ever charged. */}
            {program.price?.amount != null
              ? `$${program.price.amount}${feeUnit(program)}`
              : <span className="italic">Fee N/A</span>}
          </span>
        </div>

        {/* Enrollment bar */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-xs font-medium ${theme.textSecondary} flex items-center gap-1`}>
              <Users size={12} />
              Enrollment
            </span>
            <span className={`text-xs font-semibold ${isFull ? 'text-red-500' : theme.text}`}>
              {enrolled}{max ? `/${max}` : ''}{isFull ? ' — Full' : ''}
            </span>
          </div>
          {fillPct !== null && (
            <div className={`w-full h-1.5 rounded-full ${theme.background}`}>
              <div
                className={`h-1.5 rounded-full transition-all ${isFull ? 'bg-red-500' : fillPct >= 75 ? 'bg-orange-400' : 'bg-green-500'}`}
                style={{ width: `${fillPct}%` }}
              />
            </div>
          )}
        </div>

        {/* Registration toggle */}
        <div className={`flex items-center gap-2 text-xs ${theme.textSecondary}`}>
          {program.registrationOpen ? (
            <><CheckCircle size={12} className="text-green-500" /> Registration open</>
          ) : (
            <><AlertCircle size={12} className="text-gray-400" /> Registration closed</>
          )}
        </div>

        {/* Actions */}
        <div className={`pt-3 mt-auto border-t ${theme.border} space-y-2`}>
          {confirmDeleteId === program.id ? (
            <div className="flex items-center gap-2">
              <span className={`text-xs ${theme.textSecondary} flex-1`}>Delete this program?</span>
              <button
                onClick={() => onDelete(program.id)}
                className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs font-medium hover:bg-red-700 transition-colors"
              >
                Yes, delete
              </button>
              <button
                onClick={() => setConfirmDeleteId(null)}
                className={`px-3 py-1.5 border ${theme.border} rounded-lg text-xs ${theme.text} hover:bg-gray-50 transition-colors`}
              >
                Cancel
              </button>
            </div>
          ) : (
            // Uniform 3-up grid rather than free-flowing rows: the labels vary
            // a lot in width ("Edit" vs "Close Reg."), so a plain flex row left
            // ragged edges and broke "Close Reg." mid-label in a ~1/3-width card.
            //
            // A grid item's min-width is `auto`, so it refuses to shrink below
            // its own content — and with nowrap on the label, the longest one
            // ("Sessions") pushed its text straight out through the side of the
            // button and past the edge of the card.
            //
            // Three across only fits when the card is wide enough for the
            // longest label, and how wide the CARD is has nothing to do with
            // the viewport: it depends on the settings panel beside it. So the
            // count is a container query on the card itself — two across when
            // it is narrow, three when there is room — rather than a viewport
            // breakpoint that is right about the window and wrong about the
            // space the buttons are actually in.
            //
            // `min-w-0` and `truncate` stay as the backstop: whatever happens,
            // a label ends inside its own button.
            <div className="@container">
            <div className="grid grid-cols-2 @[19rem]:grid-cols-3 gap-2">
              <button
                onClick={() => onEdit(program)}
                className={`flex items-center justify-center gap-1 px-1.5 py-1.5 border rounded-lg text-xs min-w-0 transition-colors ${tone.neutral}`}
              >
                <Edit2 size={12} className="flex-shrink-0" /> <span className="truncate">Edit</span>
              </button>
              <button
                onClick={() => onFlyer(program)}
                className={`flex items-center justify-center gap-1 px-1.5 py-1.5 border rounded-lg text-xs font-medium min-w-0 transition-colors ${tone.flyer}`}
                title="Generate AI flyer"
              >
                <Sparkles size={12} className="flex-shrink-0" /> <span className="truncate">Flyer</span>
              </button>
              {program.flyerUrl && (
                <button
                  onClick={() => onShare(program)}
                  className={`flex items-center justify-center gap-1 px-1.5 py-1.5 border rounded-lg text-xs font-medium min-w-0 transition-colors ${tone.share}`}
                  title="Share flyer"
                >
                  <Share2 size={12} className="flex-shrink-0" /> <span className="truncate">Share</span>
                </button>
              )}
              {sellsPerSession && (
                <button
                  onClick={() => onSessions(program)}
                  className={`flex items-center justify-center gap-1 px-1.5 py-1.5 border rounded-lg text-xs font-medium min-w-0 transition-colors ${tone.assign}`}
                  title="Add the nights this runs, and price them"
                >
                  <CalendarDays size={12} className="flex-shrink-0" /> <span className="truncate">Sessions</span>
                </button>
              )}
              <button
                onClick={() => onAssign(program)}
                className={`flex items-center justify-center gap-1 px-1.5 py-1.5 border rounded-lg text-xs font-medium min-w-0 transition-colors ${tone.assign}`}
                title="Assign students to this program"
              >
                <UserPlus size={12} className="flex-shrink-0" /> <span className="truncate">Assign</span>
              </button>
              <button
                onClick={() => onToggleActive(program)}
                className={`flex items-center justify-center gap-1 px-1.5 py-1.5 border rounded-lg text-xs min-w-0 transition-colors ${tone.neutral}`}
              >
                <Archive size={12} className="flex-shrink-0" />
                <span className="truncate">{program.registrationOpen ? 'Close' : 'Open'}</span>
              </button>
              <button
                onClick={() => setConfirmDeleteId(program.id)}
                className={`flex items-center justify-center gap-1 px-1.5 py-1.5 border rounded-lg text-xs min-w-0 transition-colors ${tone.delete}`}
              >
                <Trash2 size={12} className="flex-shrink-0" /> <span className="truncate">Delete</span>
              </button>
            </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const PAGE_SIZE = 9;

const FILTER_TABS = [
  { id: 'current',  label: 'Current' },
  { id: 'all',      label: 'All' },
  { id: 'active',   label: 'Active' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'ended',    label: 'Ended' },
  { id: 'archived', label: 'Archived' },
];

export default function ProgramsSettings({ academyId, userRole, onAcademyUpdate }) {
  const { theme, isDark } = useTheme();

  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterTab, setFilterTab] = useState('current');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [flyerProgram, setFlyerProgram] = useState(null);
  const [shareProgram, setShareProgram] = useState(null);
  const [assignProgram, setAssignProgram] = useState(null);
  // Which programme is open, held in the URL like ProgramsTab does, so a
  // refresh or a shared link lands back on the same programme rather than the
  // list.
  const [detailProgramId, setDetailProgramId] = useState(
    () => new URLSearchParams(window.location.search).get('program') || null,
  );
  const [detailTab, setDetailTab] = useState('sessions');
  // The programme whose pricing modal is open. A programme-level price is what
  // a course charges outright, and what an open play session falls back to when
  // the night has no price of its own.
  const [pricingProgram, setPricingProgram] = useState(null);

  const openDetail = (program) => {
    setDetailProgramId(program.id);
    const url = new URL(window.location);
    url.searchParams.set('program', program.id);
    window.history.pushState({}, '', url);
  };
  const closeDetail = () => {
    setDetailProgramId(null);
    const url = new URL(window.location);
    url.searchParams.delete('program');
    window.history.pushState({}, '', url);
  };

  /**
   * Every programme, gathered a page at a time.
   *
   * The endpoint became paged so the public Programs tab would stop shipping an
   * academy's entire history in one response. This screen filters and pages the
   * list itself, in the browser, so it still needs all of it — and reading only
   * the first page would have quietly hidden the rest behind a search box that
   * appeared to work.
   *
   * A loop rather than one big request because the server caps a page at 100.
   * This screen should follow the Programs tab and filter server-side too; until
   * it does, this keeps its behaviour exactly as it was.
   */
  const loadPrograms = useCallback(() => {
    if (!academyId) return;
    setLoading(true);
    const LIMIT = 100;
    const all = [];
    const fetchPage = (page) =>
      academyAPI.programs.list(academyId, { page, limit: LIMIT }).then((res) => {
        all.push(...(res?.data ?? []));
        // Trust the server's own answer about whether more exist rather than
        // inferring it from the length, which stops one page early whenever the
        // total happens to be a multiple of the limit.
        if (res?.pagination?.hasNextPage) return fetchPage(page + 1);
        return all;
      });

    fetchPage(1)
      .then(list => setPrograms(list))
      .catch(() => { toast.error('Failed to load programs'); setPrograms([]); })
      .finally(() => setLoading(false));
  }, [academyId]);

  useEffect(() => { loadPrograms(); }, [loadPrograms]);
  useEffect(() => { setPage(1); }, [filterTab, search]);

  const filtered = useMemo(() => {
    let list = programs;
    if (filterTab === 'current') {
      list = list.filter(p => ['active', 'upcoming'].includes(getProgramStatus(p)));
    } else if (filterTab !== 'all') {
      list = list.filter(p => getProgramStatus(p) === filterTab);
    }
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.sportName?.toLowerCase().includes(q) ||
        p.description?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [programs, filterTab, search]);

  const counts = useMemo(() => {
    const c = { all: programs.length, current: 0, active: 0, upcoming: 0, ended: 0, archived: 0 };
    programs.forEach(p => {
      const s = getProgramStatus(p);
      c[s]++;
      if (s === 'active' || s === 'upcoming') c.current++;
    });
    return c;
  }, [programs]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const paginated = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const handleSave = async (data) => {
    try {
      if (editingProgram) {
        const updated = await academyAPI.programs.update(academyId, editingProgram.id, data);
        setPrograms(prev => prev.map(p => p.id === editingProgram.id ? updated : p));
        toast.success(`"${updated.name}" updated`);
      } else {
        const created = await academyAPI.programs.create(academyId, data);
        setPrograms(prev => [...prev, created]);
        toast.success(`"${created.name}" created`);
        onAcademyUpdate?.();
      }
      setShowModal(false);
      setEditingProgram(null);
    } catch {
      toast.error(editingProgram ? 'Failed to update program' : 'Failed to create program');
    }
  };

  const handleEdit = (program) => {
    setEditingProgram(program);
    setShowModal(true);
  };

  const handleFlyerGenerated = (programId, flyerUrl) => {
    setPrograms(prev => prev.map(p =>
      p.id === programId ? { ...p, flyerUrl } : p
    ));
  };

  const handleToggleActive = async (program) => {
    try {
      const updated = await academyAPI.programs.update(academyId, program.id, { registrationOpen: !program.registrationOpen });
      setPrograms(prev => prev.map(p => p.id === program.id ? updated : p));
      toast.success(updated.registrationOpen ? `Registration opened for "${program.name}"` : `Registration closed for "${program.name}"`);
    } catch {
      toast.error('Failed to update program');
    }
  };

  const handleDelete = async (id) => {
    const name = programs.find(p => p.id === id)?.name;
    try {
      await academyAPI.programs.remove(academyId, id);
      setPrograms(prev => prev.filter(p => p.id !== id));
      setConfirmDeleteId(null);
      toast.success(`"${name}" deleted`);
      onAcademyUpdate?.();
    } catch {
      toast.error('Failed to delete program');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader className={`w-8 h-8 animate-spin ${theme.link}`} />
      </div>
    );
  }

  const detailProgram = programs.find(p => p.id === detailProgramId) || null;

  // A full view rather than a dialog. Managing a season — adding a series,
  // pricing it, cancelling a night — is sustained work, and a modal frames that
  // as an interruption to finish and dismiss.
  if (detailProgram) {
    const sellsPerSession = detailProgram.offering?.registrationScope === 'occurrence';
    const detailTabs = [
      ...(sellsPerSession ? [{ id: 'sessions', label: 'Sessions' }] : []),
      { id: 'details', label: 'Details' },
    ];
    const activeDetailTab = detailTabs.some(t => t.id === detailTab) ? detailTab : detailTabs[0].id;

    return (
      <div className="space-y-5">
        <div>
          <button onClick={closeDetail}
            className={`flex items-center gap-1.5 text-sm ${theme.link} hover:underline mb-3`}>
            <ChevronLeft size={15} /> All programs
          </button>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <h2 className={`text-xl font-bold ${theme.text}`}>{detailProgram.name}</h2>
              <p className={`text-sm ${theme.textSecondary} mt-0.5 flex items-center gap-2 flex-wrap`}>
                <span className="capitalize">{(detailProgram.programType || 'course').replace('_', ' ')}</span>
                {detailProgram.sportName && <span>· {detailProgram.sportName}</span>}
                {detailProgram.level && <span className="capitalize">· {detailProgram.level}</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {/* Offered only where a programme-level price is what anyone
                  actually pays. Sold per session, it is priced night by night
                  on the sessions themselves — the server rejects a programme
                  price for those, so showing the button would only mislead. */}
              {!sellsPerSession && (
                <button onClick={() => setPricingProgram(detailProgram)}
                  className={`px-3 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm flex items-center gap-1.5`}>
                  <DollarSign size={14} /> Pricing
                </button>
              )}
              <button onClick={() => handleEdit(detailProgram)}
                className={`px-3 py-2 border ${theme.border} ${theme.text} ${theme.cardHover} rounded-lg text-sm`}>
                Edit
              </button>
            </div>
          </div>
        </div>

        {detailTabs.length > 1 && (
          <div className={`flex items-center gap-1 p-1 ${theme.card} rounded-xl w-fit`}>
            {detailTabs.map(t => (
              <button key={t.id} onClick={() => setDetailTab(t.id)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  activeDetailTab === t.id ? theme.tabActive : `${theme.text} ${theme.cardHover}`}`}>
                {t.label}
              </button>
            ))}
          </div>
        )}

        {activeDetailTab === 'sessions' && (
          <ProgramSessionsPanel academyId={academyId} program={detailProgram} />
        )}
        {activeDetailTab === 'details' && (
          <div className={`${theme.card} border rounded-xl p-4 space-y-2`}>
            {detailProgram.description && <p className={`text-sm ${theme.textSecondary}`}>{detailProgram.description}</p>}
            <p className={`text-sm ${theme.textMuted}`}>
              {detailProgram.startDate} → {detailProgram.endDate}
            </p>
            {/* Said plainly, because the programme card still shows a fee that
                does not apply once sessions carry their own prices. */}
            {sellsPerSession && (
              <p className={`text-xs ${theme.textMuted}`}>
                Sold per session — prices live on the sessions, not here.
              </p>
            )}
          </div>
        )}

        {showModal && (
          <CreateProgramModal
            isOpen={showModal}
            onClose={() => { setShowModal(false); setEditingProgram(null); }}
            onSave={handleSave}
            editingProgram={editingProgram}
            academyId={academyId}
          />
        )}

        {pricingProgram && (
          <AttachPriceModal
            isOpen
            onClose={() => setPricingProgram(null)}
            academyId={academyId}
            ownerType="program"
            ownerId={pricingProgram.id}
            entityName={pricingProgram.name}
          />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className={`text-xl font-bold ${theme.text}`}>Programs</h2>
          <p className={`text-sm ${theme.textSecondary} mt-0.5`}>
            {programs.length} program{programs.length !== 1 ? 's' : ''} · {counts.active} active
          </p>
        </div>
        <button
          onClick={() => { setEditingProgram(null); setShowModal(true); }}
          className={`flex items-center gap-2 px-4 py-2 ${theme.buttonPrimary} rounded-lg text-sm font-medium  transition-colors self-start`}
        >
          <Plus size={16} /> New Program
        </button>
      </div>

      {/* Toolbar: filter tabs + search */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        {/* Filter tabs */}
        <div className={`flex items-center gap-1 p-1 ${theme.card} rounded-xl flex-wrap`}>
          {FILTER_TABS.map(f => (
            <button
              key={f.id}
              onClick={() => setFilterTab(f.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                filterTab === f.id ? theme.tabActive : `${theme.text} ${theme.cardHover}`
              }`}
            >
              {f.label}
              {counts[f.id] > 0 && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${
                  filterTab === f.id ? theme.tabActive : `${theme.background} ${theme.textMuted}`
                }`}>
                  {counts[f.id]}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Search */}
        <div className={`flex items-center gap-2 px-3 py-2 border ${theme.border} rounded-lg ${theme.card} sm:w-56`}>
          <Search size={14} className={theme.textMuted} />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search programs…"
            className={`bg-transparent text-sm ${theme.text} outline-none w-full`}
          />
        </div>
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <div className={`${theme.card} rounded-xl text-center py-16 border ${theme.border}`}>
          <BookOpen size={40} className={`mx-auto mb-3 ${theme.textMuted} opacity-30`} />
          <p className={`font-semibold ${theme.text}`}>
            {search ? 'No programs match your search' : filterTab === 'current' ? 'No active or upcoming programs' : 'No programs yet'}
          </p>
          <p className={`text-sm ${theme.textMuted} mt-1`}>
            {search ? 'Try a different keyword.' : filterTab === 'current' ? 'Switch to "All" to see ended or archived programs.' : 'Click "New Program" to create your first program.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {paginated.map(program => (
            <ProgramCard
              key={program.id}
              program={program}
              onEdit={handleEdit}
              onFlyer={p => setFlyerProgram(p)}
              onShare={p => setShareProgram(p)}
              onAssign={p => setAssignProgram(p)}
              onSessions={openDetail}
              onToggleActive={handleToggleActive}
              onDelete={handleDelete}
              confirmDeleteId={confirmDeleteId}
              setConfirmDeleteId={setConfirmDeleteId}
              theme={theme}
              isDark={isDark}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-2">
          <p className={`text-sm ${theme.textMuted}`}>
            Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)} of {filtered.length}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className={`px-3 py-1.5 rounded-lg text-sm border ${theme.border} ${theme.text} ${theme.cardHover} transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              ← Prev
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
              <button
                key={n}
                onClick={() => setPage(n)}
                className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                  n === page ? theme.tabActive : `border ${theme.border} ${theme.text} ${theme.cardHover}`
                }`}
              >
                {n}
              </button>
            ))}
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className={`px-3 py-1.5 rounded-lg text-sm border ${theme.border} ${theme.text} ${theme.cardHover} transition-colors disabled:opacity-40 disabled:cursor-not-allowed`}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <CreateProgramModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setEditingProgram(null); }}
          onSave={handleSave}
          editingProgram={editingProgram}
          academyId={academyId}
        />
      )}

      {/* AI Flyer Modal */}
      {flyerProgram && (
        <AiFlyerModal
          isOpen={!!flyerProgram}
          onClose={() => setFlyerProgram(null)}
          program={flyerProgram}
          academyId={academyId}
          onFlyerGenerated={handleFlyerGenerated}
        />
      )}

      {/* Share Flyer Modal */}
      {shareProgram && (
        <ShareModal
          isOpen={!!shareProgram}
          onClose={() => setShareProgram(null)}
          entity={{ id: shareProgram.id, name: shareProgram.name, description: shareProgram.description }}
          entityType="flyer"
          urlPattern={shareProgram.flyerUrl}
        />
      )}

      {/* Assign Students Modal */}
      {assignProgram && (
        <ProgramEnrollmentModal
          isOpen={!!assignProgram}
          onClose={() => setAssignProgram(null)}
          academyId={academyId}
          program={assignProgram}
        />
      )}
    </div>
  );
}
